import { useState } from "react";

import { useTranslation } from "react-i18next";

import { MapPin } from "lucide-react";

import { useItems } from "@/api/items";
import { useLocationName } from "@/api/locations";
import { useShortages, type NodeShortages } from "@/api/planningRun";
import { Badge } from "@/components/ui/badge";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { formatDate } from "@/utils/formatters";
import { SEVERITY_SWATCH } from "@/views/items/detail/stock-chart/datasets";
import StockChart from "@/views/items/detail/stock-chart/StockChart";

import { NodeOrders } from "./NodeOrders";

// White hatch over the severity colour, so severity stays readable.
const ACCEPTED =
  "bg-[repeating-linear-gradient(135deg,rgb(255_255_255/0.6)_0_2px,transparent_2px_6px)] ring-1 ring-foreground";

const DAY_MS = 24 * 60 * 60 * 1000;

/** The shared time axis: from the plan date to the last shortage's or time
 * fence's end. */
type Axis = {
  /** Horizontal position of a date or timestamp, as a CSS percentage. */
  at: (date: string | number) => string;
  /** Month starts inside the axis, in UTC like the ISO dates. */
  months: number[];
};

function timelineAxis(planDate: string, rows: NodeShortages[]): Axis {
  const start = Date.parse(planDate);
  const end = Math.max(
    ...rows.flatMap((r) => r.shortages.map((s) => Date.parse(s.end))),
    ...rows.map((r) => (r.timeFence ? Date.parse(r.timeFence) + DAY_MS : 0)),
  );
  const months = [];
  const month = new Date(start);
  month.setUTCDate(1);
  for (month.setUTCMonth(month.getUTCMonth() + 1); +month < end;) {
    months.push(+month);
    month.setUTCMonth(month.getUTCMonth() + 1);
  }
  return {
    at: (date) => {
      const ms = typeof date === "string" ? Date.parse(date) : date;
      return `${((ms - start) / (end - start)) * 100}%`;
    },
    months,
  };
}

/** Rows grouped by location, in order of each location's first shortage. */
function byLocation(rows: NodeShortages[]): [number, NodeShortages[]][] {
  const groups = new Map<number, NodeShortages[]>();
  for (const row of rows)
    groups.set(row.locationId, [...(groups.get(row.locationId) ?? []), row]);
  return [...groups];
}

// Positions come from the data, so they are inline styles, not classes.
function MonthLabels({ axis }: { axis: Axis }) {
  return axis.months.map((month) => (
    <span
      key={month}
      className="absolute -translate-x-1/2"
      style={{ left: axis.at(month) }}
    >
      {formatDate(month, { month: "short", timeZone: "UTC" })}
    </span>
  ));
}

function MonthGridlines({ axis }: { axis: Axis }) {
  return axis.months.map((month) => (
    <span
      key={month}
      className="bg-border/70 absolute inset-y-0 w-px"
      style={{ left: axis.at(month) }}
    />
  ));
}

function Stretches({ row, axis }: { row: NodeShortages; axis: Axis }) {
  const { t } = useTranslation();
  return row.shortages.map((s) => (
    <span
      key={s.start}
      data-severity={s.severity}
      data-accepted={s.accepted || undefined}
      title={[
        `${formatDate(s.start)} – ${formatDate(s.end)}`,
        t(`items.stockChart.${s.severity}`),
        s.accepted && t("planningReview.shortages.accepted", "Accepted"),
      ]
        .filter(Boolean)
        .join(" · ")}
      className={cn(
        "absolute top-1/2 h-2 min-w-0.5 -translate-y-1/2 rounded-full",
        SEVERITY_SWATCH[s.severity],
        s.accepted && ACCEPTED,
      )}
      style={{
        left: axis.at(s.start),
        width: `calc(${axis.at(s.end)} - ${axis.at(s.start)})`,
      }}
    />
  ));
}

function FenceTick({ timeFence, axis }: { timeFence: string; axis: Axis }) {
  const { t } = useTranslation();
  return (
    <span
      title={t("planningReview.shortages.timeFence", "Skip through {{date}}", {
        date: formatDate(timeFence),
      })}
      className="bg-foreground absolute inset-y-2 w-0.5"
      // The fence holds through its date, so the tick sits at that day's end.
      style={{ left: axis.at(Date.parse(timeFence) + DAY_MS) }}
    />
  );
}

function Legend() {
  const { t } = useTranslation();
  return (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4">
      {(["shortage", "stockout"] as const).map((severity) => (
        <span key={severity} className="flex items-center gap-1.5">
          <span
            className={cn("h-2 w-3 rounded-full", SEVERITY_SWATCH[severity])}
          />
          {t(`items.stockChart.${severity}`)}
        </span>
      ))}
      <span className="flex items-center gap-1.5">
        <span
          className={cn(
            "h-2 w-3 rounded-full",
            SEVERITY_SWATCH.stockout,
            ACCEPTED,
          )}
        />
        {t("planningReview.shortages.accepted", "Accepted")}
      </span>
      <span className="flex items-center gap-1.5">
        <span className="bg-foreground h-3 w-0.5" />
        {t("planningReview.shortages.timeFenceLegend", "Time fence")}
      </span>
    </span>
  );
}

export function ShortagesView({
  runId,
  planDate,
}: {
  runId: number;
  planDate: string;
}) {
  const { t } = useTranslation();
  const shortages = useShortages(runId);
  const rows = shortages.data ?? [];
  const items = useItems({
    ids: [...new Set(rows.map((r) => r.itemId))],
    limit: rows.length,
  });
  const locationName = useLocationName();
  const [open, setOpen] = useState<NodeShortages | null>(null);

  if (shortages.isLoading)
    return (
      <div className="flex flex-col gap-2 p-4">
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className="h-7" />
        ))}
      </div>
    );
  if (rows.length === 0)
    return (
      <Empty className="h-full">
        <EmptyHeader>
          <EmptyTitle>
            {t("planningReview.shortages.emptyTitle", "No shortages")}
          </EmptyTitle>
          <EmptyDescription>
            {t(
              "planningReview.shortages.emptyDescription",
              "The plan keeps every node above the level the engine orders against.",
            )}
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );

  const item = (id: number) => items.data?.results.find((i) => i.id === id);
  const axis = timelineAxis(planDate, rows);

  return (
    <div className="h-full overflow-auto text-sm">
      <div className="bg-background text-muted-foreground sticky top-0 z-10 flex flex-col gap-2 border-b py-2 text-xs">
        <Legend />
        <div className="flex">
          <div className="w-72 shrink-0" />
          <div className="relative mr-6 h-4 flex-1">
            <MonthLabels axis={axis} />
          </div>
        </div>
      </div>
      {byLocation(rows).map(([locationId, group]) => (
        <section key={locationId} aria-label={locationName(locationId)}>
          <h3 className="bg-muted/40 text-muted-foreground flex items-center gap-2 border-b px-4 py-1.5 text-xs font-medium">
            <MapPin className="size-3.5" />
            {locationName(locationId)}
            <Badge variant="secondary">{group.length}</Badge>
          </h3>
          <ul>
            {group.map((row) => (
              <li key={row.itemId}>
                <button
                  type="button"
                  onClick={() => setOpen(row)}
                  className="hover:bg-muted/50 flex h-11 w-full items-center border-b text-left"
                >
                  <span className="flex w-72 shrink-0 items-baseline gap-2 px-4">
                    <span className="truncate">{item(row.itemId)?.name}</span>
                    <span className="text-muted-foreground shrink-0 text-xs">
                      {item(row.itemId)?.sku}
                    </span>
                  </span>
                  <span className="relative mr-6 h-full flex-1">
                    <MonthGridlines axis={axis} />
                    <Stretches row={row} axis={axis} />
                    {row.timeFence && (
                      <FenceTick timeFence={row.timeFence} axis={axis} />
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
      <Sheet open={open !== null} onOpenChange={(o) => !o && setOpen(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-4xl">
          {open && (
            <>
              <SheetHeader>
                <SheetTitle>
                  {item(open.itemId)?.name} · {locationName(open.locationId)}
                </SheetTitle>
                <SheetDescription>
                  {t(
                    "planningReview.shortages.firstShortage",
                    "First shortage {{date}}",
                    { date: formatDate(open.shortages[0].start) },
                  )}
                </SheetDescription>
              </SheetHeader>
              <div className="space-y-4 px-4 pb-4">
                <StockChart
                  itemId={open.itemId}
                  locationId={open.locationId}
                  runId={runId}
                />
                <NodeOrders
                  runId={runId}
                  itemId={open.itemId}
                  locationId={open.locationId}
                />
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
